import type { HTMLAttributes, ReactNode } from 'react'
import { StatTile, type StatTileProps } from '../controls/StatTile'
import { cx } from '../controls/cx'
import type { Tone } from '../controls/Pill'
import s from './StatStrip.module.css'

/**
 * THE SUMMARY STRIP (#2283 §2) — one geometry, and one rule about not lying.
 *
 * ── what was measured, and why this is an extraction rather than a new idea ─
 * `StatTile` has been shared for a long time. What was never shared is the
 * CONTAINER it sits in, and the two hosts had grown **26 of them in 12 declared
 * shapes**. Nine were byte-identical (`display: flex; gap: var(--sp-3);
 * flex-wrap: wrap`), and the rest disagreed about the one thing a reader can
 * actually see — how wide a tile is allowed to get before the row wraps:
 *
 *     flex-wrap, no minimum        x9   access x4 · comms · estate ·
 *                                       interactions · marketplace · releases
 *     minmax(180px, 1fr)           x2   funding · time review
 *     minmax(190px, 1fr)           x1   catalog
 *     minmax(200px, 1fr)           x2   console home (twice)
 *     minmax(min(9.5rem, 100%))    x1   MeasureStrip
 *     repeat(4, minmax(0, 1fr))    x1   portal commercial overview
 *     flex, gap --sp-2-5           x1   personas
 *
 * ── the one that was chosen, and why it is not a vote ──────────────────────
 * `repeat(auto-fit, minmax(min(9.5rem, 100%), 1fr))`, from `MeasureStrip`. It
 * is the only shape here that cannot overflow its own container AT ANY WIDTH:
 * a `minmax(<fixed>, 1fr)` track is laid at its minimum however little room
 * there is, so in a rail, a peek pane or a split's narrow side it hangs out —
 * measured at 30px of spill from a 150px container, by
 * `apps/console/e2e-browser/page-composition.spec.ts`. `min(9.5rem, 100%)`
 * yields to whatever room there is.
 *
 * That claim used to be written as "180px overflows the automotive frame", and
 * it was wrong: 180px fits the main content column at all four profiles, and
 * the mutation that put it back came back GREEN. The green is the reason this
 * paragraph says a container rather than a viewport.
 *
 * `auto-fit` with `1fr` then gives EQUAL columns, which is what the flex
 * variants gave up: nine surfaces rendered tiles of different widths in a
 * ragged row, so the numbers a reader is there to compare did not line up. The
 * portal's `repeat(4, minmax(0, 1fr))` does not overflow either — it cannot
 * wrap, so four tiles are squeezed to whatever a quarter of the surface is.
 *
 * ── the rule that is worth more than the geometry ──────────────────────────
 * A tile that cannot be computed SAYS SO. `value: null` renders an em dash and
 * the reason, never a zero — because the failure this replaces produced a
 * *number*, and there was nothing for anyone to notice. `MeasureStrip` has held
 * that rule since TEC-02/UX-01 and it belongs to every strip, not to the one
 * that happens to read `core.measure`.
 *
 * This component knows nothing about measures, entities or doors: it takes
 * values that have already been computed and governed, which is the seam
 * `ui-and-experience.md` §1 draws. `MeasureStrip` is its binding in the
 * Console; a host with a count in hand passes the count.
 */
export interface StatStripTile {
  /** Stable identity for the tile — a measure key, a column name, anything. */
  key: string
  label: ReactNode
  /**
   * The value, or `null` when it could not be computed. `null` is not "zero
   * yet" — it is the honest absence, and it renders as one.
   */
  value: ReactNode | null
  /** Why there is no value. Shown in place of `sub` when `value` is null. */
  unavailable?: ReactNode
  sub?: ReactNode
  accent?: boolean
  tone?: Tone
  /** The cell's glyph, unit, delta and trend (#2406, "Dashboards and Cards":
   *  each cell is label, figure with its unit, an optional sparkline and a foot
   *  line). All four describe a value, so a cell with NO value draws none of
   *  them — "— seats" or a trend under a dash would dress an absence up as a
   *  reading. */
  icon?: StatTileProps['icon']
  unit?: StatTileProps['unit']
  delta?: StatTileProps['delta']
  trend?: StatTileProps['trend']
}

export interface StatStripProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /**
   * REST PROPS REACH THE ELEMENT, so a host can attach its own provenance to
   * the strip without wrapping it in a div that exists only to hold an
   * attribute. Console Home carries `data-dashboard-composition` and
   * `data-dashboard-items` — the seam that says whether the governed recipe
   * resolved or the outage fallback is showing — and before this it had to
   * hang them on a heading beside the figures they describe.
   */
  tiles: readonly StatStripTile[]
  /** Every value is still being read. Tiles render a placeholder, not a zero. */
  loading?: boolean
  /** Names the group for a screen reader, when the surrounding heading does not. */
  label?: string
  className?: string
  testId?: string
}

/** What a tile with nothing to show renders instead of a number. */
export const NO_VALUE = '—'

export function StatStrip({ tiles, loading, label, className, testId, ...rest }: StatStripProps) {
  return (
    <div
      className={cx(s.strip, className)}
      data-testid={testId}
      data-stat-strip=""
      role={label != null ? 'group' : undefined}
      aria-label={label}
      aria-busy={loading || undefined}
      {...rest}
    >
      {tiles.map((tile) => {
        const missing = tile.value == null
        return (
          <StatTile
            key={tile.key}
            label={tile.label}
            value={loading ? '…' : missing ? NO_VALUE : tile.value}
            /* The reason replaces the sub-line rather than joining it: the tile
               is small, and a gap explained under a caption nobody asked for is
               how "no data" ends up looking like a footnote on a real figure. */
            sub={loading ? undefined : missing ? tile.unavailable : tile.sub}
            accent={tile.accent}
            tone={tile.tone}
            icon={tile.icon}
            unit={loading || missing ? undefined : tile.unit}
            delta={loading || missing ? undefined : tile.delta}
            trend={loading || missing ? undefined : tile.trend}
          />
        )
      })}
    </div>
  )
}
